// react
import { FormEvent, useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, Stack, Typography } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// ui
import { Loading } from '../../ui/Loading/Loading';

// dialogs
import { DialogActions, DialogCloseEvent } from '../DialogActions/DialogActions';

// props
export interface DialogProjectReportProps {
	isOpen: boolean;
	onClose: (e?: DialogCloseEvent) => void;
}

export const DialogProjectReport = ({ isOpen, onClose }: DialogProjectReportProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [values, setValues] = useState({
		dateEnd: '',
		dateStart: '',
	});

	// report fetch (lazy — triggered on submit)
	const { fetchRequest: fetchReport, isLoading } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/reports?type=PROJECT_STATS`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: () => {
			setBanner({
				hasClose: true,
				message: t('alert.reportSent'),
				severity: 'success',
			});
			onClose();
		},
		onError: () => {
			setBanner({
				hasClose: true,
				message: t('alert.reportFailed'),
				severity: 'error',
			});
			onClose();
		},
	});

	// today (YYYY-MM-DD) — used to prevent selecting future dates
	const today = new Date().toISOString().slice(0, 10);

	// validation guards
	const isRangeInvalid =
		Boolean(values.dateStart) && Boolean(values.dateEnd) && values.dateStart > values.dateEnd;
	const isStartInFuture = Boolean(values.dateStart) && values.dateStart > today;
	const isEndInFuture = Boolean(values.dateEnd) && values.dateEnd > today;

	// vars
	const idForm = 'form-project-report';

	// handlers
	const handleSend = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const params = new URLSearchParams({ type: 'PROJECT_STATS' });

		if (values.dateStart) {
			params.append('dateStart', new Date(values.dateStart).toISOString());
		}

		if (values.dateEnd) {
			params.append('dateEnd', new Date(values.dateEnd).toISOString());
		}

		fetchReport(`${process.env.REACT_APP_API_URL}/reports?${params.toString()}`);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: onClose,
					variant: 'text',
				},
				{
					children: t('send'),
					disabled: isLoading || isRangeInvalid || isStartInFuture || isEndInFuture,
					form: idForm,
					loading: isLoading,
					type: 'submit',
					variant: 'contained',
				},
			]}
			isOpen={isOpen}
			maxWidth={400}
			onClose={onClose}
			title={{ children: t('emailReport') }}>
			{isLoading ? (
				<Grid alignItems="center" container justifyContent="center">
					<Grid item>
						<Loading />
					</Grid>
				</Grid>
			) : (
				<form id={idForm} onSubmit={handleSend}>
					<Stack spacing={2}>
						<Typography>{t('emailFinancialReport.dateRange.description')}</Typography>
						<Input
							error={isStartInFuture}
							helperText={isStartInFuture ? t('dateRange.noFuture') : undefined}
							id="report-date-start"
							inputProps={{ max: today }}
							label={t('startDate')}
							onChange={(e) => setValues((prev) => ({ ...prev, dateStart: e.target.value }))}
							type="date"
							value={values.dateStart}
						/>
						<Input
							error={isRangeInvalid || isEndInFuture}
							helperText={
								isEndInFuture
									? t('dateRange.noFuture')
									: isRangeInvalid
									? t('dateRange.invalid')
									: undefined
							}
							id="report-date-end"
							inputProps={{ max: today, min: values.dateStart || undefined }}
							label={t('endDate')}
							onChange={(e) => setValues((prev) => ({ ...prev, dateEnd: e.target.value }))}
							type="date"
							value={values.dateEnd}
						/>
					</Stack>
				</form>
			)}
		</DialogActions>
	);
};
